import React, { useState, useCallback, useEffect } from 'react';
import {
    Button,
    CardComponent,
    AlertComponent,
    InputComponent,
} from '../../../components/shared';
import { Input } from 'antd';
import { useTranslation } from 'react-i18next';

export interface LoginProps {
    formMode: 'LOGIN' | 'CREATE_NEW_PASSWORD';
    title: string;
    errorMessage: string;
    passwordErrMsg: string;
    username: string;
    clickGate: boolean;
    onUserNameChange: (value: string) => void;
    password: string;
    onChangePassword: (value: string) => void;
    confirmPassword: string;
    onChangeConfirmPassword: (value: string) => void;
    onForgotPassword: () => void;
    onSubmit: () => void;
    /** When true, shows "Sign in with SSO" button. Clicking switches to customer_id form. */
    ssoEnabled?: boolean;
    onSSOLogin?: (customerId: string) => void;
    /** Called when user clicks "Sign in with SSO" to switch to the customer_id form. */
    onShowSSOForm?: () => void;
    /** When true, shows the SSO customer_id form instead of email/password. */
    showSSOForm?: boolean;
    onBackFromSSO?: () => void;
    tenantId?: string;
    onTenantIdChange?: (value: string) => void;
}

const Login: React.FC<LoginProps> = ({
    formMode = 'LOGIN',
    title = '',
    errorMessage = '',
    passwordErrMsg = '',
    username = '',
    clickGate = false,
    onUserNameChange = () => { },
    password = '',
    onChangePassword = () => { },
    confirmPassword = '',
    onChangeConfirmPassword = () => { },
    onForgotPassword = () => { },
    onSubmit = () => { },
    ssoEnabled = false,
    onSSOLogin,
    onShowSSOForm,
    showSSOForm = false,
    onBackFromSSO,
    tenantId = '',
    onTenantIdChange,
}) => {
    const { t } = useTranslation();
    const handleKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Enter') {
                onSubmit();
            }
        },
        [onSubmit]
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    const [passwordVisible, setPasswordVisible] = useState(false); // shows the password field when 'eye' icon is clicked
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false); // shows the confirm password field when 'eye' icon is clicked

    // SSO tenant_id form view
    if (showSSOForm) {
        const handleSSOSubmit = () => {
            if (tenantId?.trim() && onSSOLogin) {
                onSSOLogin(tenantId.trim());
            }
        };
        return (
            <CardComponent title={t('auth.signInWithSSO')} className='login-card'>
                {errorMessage && <AlertComponent className='alertText' message={errorMessage} type='error' />}
                <div className='fieldView'>
                    <label className='labelView'>{t('auth.tenantId')}</label>
                    <InputComponent
                        value={tenantId}
                        placeholder={t('auth.tenantIdPlaceholder')}
                        onChangeEvent={(e) => onTenantIdChange?.(e.target.value)}
                        disabled={clickGate}
                    />
                </div>
                <div>
                    <div className='signBtnView'>
                        <Button
                            type='primary'
                            onClickEvent={handleSSOSubmit}
                            disabled={clickGate || !tenantId?.trim()}
                            className='SignBtn'
                        >
                            {t('auth.continueToSSO')}
                        </Button>
                    </div>
                    <div className='signBtnView'>
                        <Button type='link' onClickEvent={onBackFromSSO} className='forgetPassword'>
                            {t('auth.backToLogin')}
                        </Button>
                    </div>
                </div>
            </CardComponent>
        );
    }

    return (
        <CardComponent title={title} className='login-card'>
            {errorMessage && <AlertComponent className='alertText' message={errorMessage} type='error' />}
            {passwordErrMsg && <AlertComponent className='alertText' message={passwordErrMsg} type='error' />}
            <div className='fieldView'>
                <label className='labelView emaillable'>{t('auth.email')}</label>
                <InputComponent
                    value={username}
                    placeholder={t('auth.email')}
                    onChangeEvent={(e) => {
                        onUserNameChange(e.target.value);
                    }}
                    disabled={clickGate}
                />
            </div>
            <div className='fieldView'>
                <label className='labelView'>{formMode === 'LOGIN' ? t('auth.password') : t('auth.newPassword')}</label>
                <Input.Password
                    value={password}
                    placeholder={t('auth.password')}
                    onChange={(e) => onChangePassword(e?.target?.value)}
                    disabled={clickGate}
                    className='password'
                    visibilityToggle={{ visible: passwordVisible, onVisibleChange: setPasswordVisible }}
                />
            </div>
            {formMode === 'CREATE_NEW_PASSWORD' ? (
                <div className='fieldView'>
                    <label className='labelView'>{t('auth.confirmPassword')}</label>

                    <Input.Password
                        className='confirmPassword'
                        value={confirmPassword}
                        placeholder={t('auth.confirmPassword')}
                        onChange={(e) => onChangeConfirmPassword(e?.target?.value)}
                        visibilityToggle={{
                            visible: confirmPasswordVisible,
                            onVisibleChange: setConfirmPasswordVisible,
                        }}
                    />
                </div>
            ) : null}
            <div>
                {formMode === 'LOGIN' && (
                    <div className='signBtnView'>
                        <Button type='link' onClickEvent={onForgotPassword} className='forgetPassword'>
                            {t('auth.forgotPassword')}
                        </Button>
                    </div>
                )}
                <div className='signBtnView'>
                    <Button
                        type='primary'
                        onClickEvent={onSubmit}
                        onKeyPress={handleKeyDown}
                        disabled={clickGate}
                        className='SignBtn'
                    >
                        {formMode === 'CREATE_NEW_PASSWORD' ? t('auth.setPassword') : t('auth.login')}
                    </Button>
                </div>
                {formMode === 'LOGIN' && ssoEnabled && onShowSSOForm && (
                    <div className='ssoView' style={{ marginTop: 16 }}>
                        <div style={{ marginBottom: 8, fontSize: 12, color: 'var(--text-secondary, #666)', textAlign: 'center' }}>
                            {t('auth.orSignInWith')}
                        </div>
                        <Button
                            type='default'
                            onClickEvent={onShowSSOForm}
                            disabled={clickGate}
                            className='ssoBtn'
                            style={{ width: '100%' }}
                        >
                            {t('auth.signInWithSSO')}
                        </Button>
                    </div>
                )}
            </div>
        </CardComponent>
    );
};

export default Login;